import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { getEstablishmentProfile, getPharmacySettings } from "@/core/admin/establishment-store";
import {
  getDashboardStats,
  listValidatedInvoices,
} from "@/core/pharmacy/pharmacy-store";
import type { ValidatedInvoice } from "@/core/pharmacy/types";

export interface RevenueBridgeInvoice {
  externalId: string;
  number: string;
  validatedAt: string;
  subtotal: number;
  currency: string;
  customerName: string;
  lineCount: number;
  patientId?: string;
  prescriptionId?: string;
  source: "hospitol.pharmacy";
}

export interface RevenueBridgeExport {
  version: 1;
  exportedAt: string;
  tenantSlug: string;
  establishment: string;
  currency: string;
  summary: {
    totalRevenue: number;
    revenueToday: number;
    invoiceCount: number;
  };
  invoices: RevenueBridgeInvoice[];
}

function mapInvoice(inv: ValidatedInvoice, currency: string): RevenueBridgeInvoice {
  return {
    externalId: inv.id,
    number: inv.number,
    validatedAt: inv.validatedAt,
    subtotal: inv.subtotal,
    currency,
    customerName: inv.customer.name,
    lineCount: inv.lines.length,
    ...(inv.patientId ? { patientId: inv.patientId } : {}),
    ...(inv.prescriptionId ? { prescriptionId: inv.prescriptionId } : {}),
    source: "hospitol.pharmacy",
  };
}

/** Payload consolidé pour intégration billing externe (sans Kanakku complet). */
export function buildRevenueBridgeExport(): RevenueBridgeExport {
  const invoices = listValidatedInvoices();
  const stats = getDashboardStats();
  const { currency } = getPharmacySettings();
  const tenantSlug = loadStoredSession()?.tenant?.slug ?? "demo";
  const establishment = getEstablishmentProfile().name;

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    tenantSlug,
    establishment,
    currency,
    summary: {
      totalRevenue: stats.revenueTotal,
      revenueToday: stats.revenueToday,
      invoiceCount: stats.invoiceCount,
    },
    invoices: invoices.map((inv) => mapInvoice(inv, currency)),
  };
}

export function downloadRevenueBridgeJson(): void {
  const payload = buildRevenueBridgeExport();
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `hospitol-revenue-bridge-${payload.exportedAt.slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
